/**
 * SyncEngine -- Offline sync with conflict resolution for AWENI.
 *
 * Queues offline mutations into IndexedDB, processes them FIFO per module,
 * with exponential backoff and per-action-type conflict resolution.
 */

import { offlineManager, type PendingAction, STORES } from './OfflineManager';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type ConflictStrategy = 'server-wins' | 'client-wins' | 'last-write-wins';

export interface SyncAction {
  moduleId: string;
  action: string;
  url: string;
  method: 'POST' | 'PUT' | 'DELETE';
  payload: unknown;
  headers?: Record<string, string>;
  maxRetries?: number;
  conflictStrategy?: ConflictStrategy;
  /** Optional timestamp for last-write-wins resolution */
  timestamp?: number;
}

export interface SyncStatus {
  isOnline: boolean;
  pendingCount: number;
  failedCount: number;
  lastSyncAt: number | null;
  syncInProgress: boolean;
}

export interface SyncResult {
  processed: number;
  succeeded: number;
  failed: number;
  errors: Array<{ actionId: string; message: string }>;
}

type SyncEventType = 'sync-start' | 'sync-complete' | 'sync-error' | 'action-synced' | 'action-failed';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const MAX_RETRIES_DEFAULT = 5;
const BACKOFF_BASE_MS = 1000;
const BACKOFF_MAX_MS = 16000;

/**
 * Conflict resolution strategy per action type.
 * - game scores: server is authoritative (immutable once recorded)
 * - memoriz reviews: client wins (FSRS state is computed locally)
 * - expenses: last-write-wins with timestamp comparison
 */
const CONFLICT_MAP: Record<string, ConflictStrategy> = {
  'game-score': 'server-wins',
  'game-session-complete': 'server-wins',
  'memoriz-review': 'client-wins',
  'memoriz-card-update': 'client-wins',
  'deck-review': 'client-wins',
  'expense-create': 'last-write-wins',
  'expense-update': 'last-write-wins',
  'expense-delete': 'last-write-wins',
  'transaction-create': 'last-write-wins',
  'transaction-update': 'last-write-wins',
};

// ---------------------------------------------------------------------------
// SyncEngine
// ---------------------------------------------------------------------------

export class SyncEngine {
  private syncing = false;
  private emitter = new EventTarget();
  private onlineHandler: (() => void) | null = null;
  private swMessageHandler: ((event: MessageEvent) => void) | null = null;

  // ---- Lifecycle ----------------------------------------------------------

  init(): void {
    // Listen for online events to trigger sync
    this.onlineHandler = () => {
      if (navigator.onLine) {
        this.processQueue();
      }
    };
    window.addEventListener('online', this.onlineHandler);

    // Listen for SW messages (SYNC_COMPLETE, etc.)
    this.swMessageHandler = (event: MessageEvent) => {
      if (event.data?.type === 'SYNC_COMPLETE') {
        this.processQueue();
      }
    };
    navigator.serviceWorker?.addEventListener('message', this.swMessageHandler);

    // Try to register background sync
    this.registerBackgroundSync();
  }

  destroy(): void {
    if (this.onlineHandler) {
      window.removeEventListener('online', this.onlineHandler);
    }
    if (this.swMessageHandler && navigator.serviceWorker) {
      navigator.serviceWorker.removeEventListener('message', this.swMessageHandler);
    }
  }

  // ---- Public API ---------------------------------------------------------

  /**
   * Queue an offline action. Returns the action ID.
   */
  async queueAction(action: SyncAction): Promise<string> {
    const id = crypto.randomUUID();
    const idempotencyKey = crypto.randomUUID();

    const pending: PendingAction = {
      id,
      idempotency_key: idempotencyKey,
      moduleId: action.moduleId,
      action: action.action,
      url: action.url,
      method: action.method,
      payload: action.payload,
      headers: {
        'Content-Type': 'application/json',
        ...(action.headers ?? {}),
        'Idempotency-Key': idempotencyKey,
      },
      status: 'pending',
      createdAt: action.timestamp ?? Date.now(),
      syncedAt: null,
      retryCount: 0,
      maxRetries: action.maxRetries ?? MAX_RETRIES_DEFAULT,
      lastError: null,
      serverResponse: null,
    };

    await offlineManager.addPendingAction(pending);

    // If online, start processing immediately
    if (navigator.onLine) {
      this.processQueue();
    } else {
      // Try background sync
      this.registerBackgroundSync();
    }

    return id;
  }

  /**
   * Process the pending queue. Processes FIFO per module.
   */
  async processQueue(): Promise<SyncResult> {
    if (this.syncing || !navigator.onLine) {
      return { processed: 0, succeeded: 0, failed: 0, errors: [] };
    }

    this.syncing = true;
    this.emit('sync-start', {});

    const result: SyncResult = { processed: 0, succeeded: 0, failed: 0, errors: [] };

    try {
      const pendingActions = await offlineManager.getPendingActions();

      // Sort by createdAt (FIFO) — already in order from index, but ensure
      pendingActions.sort((a, b) => a.createdAt - b.createdAt);

      // Group by module for ordered processing
      const byModule = new Map<string, PendingAction[]>();
      for (const action of pendingActions) {
        if (!byModule.has(action.moduleId)) byModule.set(action.moduleId, []);
        byModule.get(action.moduleId)!.push(action);
      }

      // Process each module's queue sequentially
      for (const [, actions] of byModule) {
        for (const action of actions) {
          result.processed++;

          try {
            await this.processAction(action);
            result.succeeded++;
            this.emit('action-synced', { actionId: action.id });
          } catch (err) {
            const message = err instanceof Error ? err.message : String(err);
            result.failed++;
            result.errors.push({ actionId: action.id, message });
            this.emit('action-failed', { actionId: action.id, error: message });

            // If this action failed with a non-retriable error, don't block the queue
            // But if it's a network error, stop processing this module
            if (this.isNetworkError(err)) break;
          }
        }
      }

      // Update last sync time
      await offlineManager.setLastSync();
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      this.emit('sync-error', { error: message });
    } finally {
      this.syncing = false;
      this.emit('sync-complete', result);
    }

    return result;
  }

  /**
   * Get count of pending (not yet synced) actions.
   */
  async getPendingCount(): Promise<number> {
    return offlineManager.getPendingCount();
  }

  /**
   * Get all failed actions for user review.
   */
  async getFailedActions(): Promise<PendingAction[]> {
    return offlineManager.getFailedActions();
  }

  /**
   * Retry all failed actions by resetting them to pending.
   */
  async retryFailed(): Promise<number> {
    const failed = await offlineManager.getFailedActions();
    let count = 0;
    for (const action of failed) {
      await offlineManager.updateActionStatus(action.id, 'pending', {
        retryCount: 0,
        lastError: null,
      });
      count++;
    }

    if (count > 0 && navigator.onLine) {
      this.processQueue();
    }

    return count;
  }

  /**
   * Get current sync status.
   */
  async getStatus(): Promise<SyncStatus> {
    const [pendingCount, failedActions, lastSync] = await Promise.all([
      offlineManager.getPendingCount(),
      offlineManager.getFailedActions(),
      offlineManager.getLastSync(),
    ]);

    return {
      isOnline: navigator.onLine,
      pendingCount,
      failedCount: failedActions.length,
      lastSyncAt: lastSync,
      syncInProgress: this.syncing,
    };
  }

  // ---- Event system -------------------------------------------------------

  on(event: SyncEventType, handler: (detail: unknown) => void): void {
    this.emitter.addEventListener(event, ((e: CustomEvent) => {
      handler(e.detail);
    }) as EventListener);
  }

  // ---- Internal: process a single action ----------------------------------

  private async processAction(action: PendingAction): Promise<void> {
    // Mark as syncing
    await offlineManager.updateActionStatus(action.id, 'syncing');

    const backoffMs = Math.min(
      BACKOFF_BASE_MS * Math.pow(2, action.retryCount),
      BACKOFF_MAX_MS,
    );

    try {
      const response = await fetch(action.url, {
        method: action.method,
        headers: action.headers,
        body: action.payload ? JSON.stringify(action.payload) : undefined,
        credentials: 'include',
      });

      if (response.ok) {
        const serverData = await response.json().catch(() => null);
        await offlineManager.updateActionStatus(action.id, 'synced', {
          serverResponse: serverData,
        });

        // If server returned an ID, update the local record
        if (serverData?.id) {
          await this.updateLocalWithServerId(action, serverData);
        }
        return;
      }

      // Handle conflict (409)
      if (response.status === 409) {
        const serverData = await response.json().catch(() => null);
        const resolved = await this.resolveConflict(action, serverData);
        if (resolved) {
          await offlineManager.updateActionStatus(action.id, 'synced', {
            serverResponse: serverData,
          });
          return;
        }
      }

      // Non-retriable HTTP errors (4xx except 409, 429)
      if (response.status >= 400 && response.status < 500 && response.status !== 429) {
        const errorBody = await response.text().catch(() => '');
        await offlineManager.updateActionStatus(action.id, 'failed', {
          lastError: `HTTP ${response.status}: ${errorBody.slice(0, 200)}`,
          retryCount: action.maxRetries, // Don't retry
        });
        return;
      }

      // Retriable errors (5xx, 429) — increment retry
      throw new Error(`HTTP ${response.status}`);
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      const newRetryCount = action.retryCount + 1;

      if (newRetryCount >= action.maxRetries) {
        await offlineManager.updateActionStatus(action.id, 'failed', {
          retryCount: newRetryCount,
          lastError: message,
        });
      } else {
        // Back to pending with incremented retry count
        await offlineManager.updateActionStatus(action.id, 'pending', {
          retryCount: newRetryCount,
          lastError: message,
        });

        // Wait with exponential backoff before next action
        await this.sleep(backoffMs);
      }

      throw err; // Propagate to caller for result tracking
    }
  }

  // ---- Conflict resolution ------------------------------------------------

  private async resolveConflict(
    action: PendingAction,
    serverData: Record<string, unknown> | null,
  ): Promise<boolean> {
    const strategy =
      CONFLICT_MAP[action.action] ?? 'last-write-wins';

    switch (strategy) {
      case 'server-wins': {
        // Accept server version, discard our change
        // Update local IndexedDB with server data if available
        if (serverData) {
          await this.applyServerData(action.moduleId, action.action, serverData);
        }
        return true;
      }

      case 'client-wins': {
        // Re-send with force flag
        try {
          const response = await fetch(action.url, {
            method: action.method,
            headers: {
              ...action.headers,
              'X-Force-Overwrite': 'true',
            },
            body: action.payload ? JSON.stringify(action.payload) : undefined,
            credentials: 'include',
          });
          return response.ok;
        } catch {
          return false;
        }
      }

      case 'last-write-wins': {
        // Compare timestamps — if server is newer, accept; otherwise force ours
        const serverTimestamp = serverData?.updatedAt
          ? new Date(serverData.updatedAt as string).getTime()
          : 0;

        if (serverTimestamp > action.createdAt) {
          // Server is newer — accept
          if (serverData) {
            await this.applyServerData(action.moduleId, action.action, serverData);
          }
          return true;
        }

        // Our version is newer — force
        try {
          const response = await fetch(action.url, {
            method: action.method,
            headers: {
              ...action.headers,
              'X-Force-Overwrite': 'true',
            },
            body: action.payload ? JSON.stringify(action.payload) : undefined,
            credentials: 'include',
          });
          return response.ok;
        } catch {
          return false;
        }
      }
    }
  }

  // ---- Local data updates after sync --------------------------------------

  private async updateLocalWithServerId(
    action: PendingAction,
    serverData: Record<string, unknown>,
  ): Promise<void> {
    // Map action types to stores for local ID updates
    const storeMap: Record<string, string> = {
      'game-score': STORES.GAME_SESSIONS,
      'game-session-complete': STORES.GAME_SESSIONS,
      'memoriz-review': STORES.MEMORIZ_REVIEWS,
      'deck-review': STORES.MEMORIZ_REVIEWS,
      'expense-create': STORES.BALANCETAB_TRANSACTIONS,
      'transaction-create': STORES.BALANCETAB_TRANSACTIONS,
    };

    const storeName = storeMap[action.action];
    if (!storeName || !serverData.id) return;

    // If the action payload had a local temp ID, update it
    const payload = action.payload as Record<string, unknown> | null;
    const localId = payload?.localId ?? payload?.id;
    if (localId && localId !== serverData.id) {
      try {
        const existing = await offlineManager.get(storeName as never, localId as IDBValidKey);
        if (existing) {
          await offlineManager.delete(storeName as never, localId as IDBValidKey);
          await offlineManager.put(storeName as never, {
            ...(existing as Record<string, unknown>),
            ...serverData,
          });
        }
      } catch {
        // Store may not exist yet — non-critical
      }
    }
  }

  private async applyServerData(
    moduleId: string,
    action: string,
    serverData: Record<string, unknown>,
  ): Promise<void> {
    // Apply server-wins data to local stores
    const storeMap: Record<string, string> = {
      'game-score': STORES.GAME_SESSIONS,
      'game-session-complete': STORES.GAME_SESSIONS,
      'expense-create': STORES.BALANCETAB_TRANSACTIONS,
      'expense-update': STORES.BALANCETAB_TRANSACTIONS,
      'transaction-create': STORES.BALANCETAB_TRANSACTIONS,
      'transaction-update': STORES.BALANCETAB_TRANSACTIONS,
    };

    const storeName = storeMap[action];
    if (!storeName || !serverData.id) return;

    try {
      await offlineManager.put(storeName as never, serverData);
    } catch {
      // Non-critical — data will refresh on next fetch
    }
  }

  // ---- Background sync registration --------------------------------------

  private async registerBackgroundSync(): Promise<void> {
    try {
      const reg = await navigator.serviceWorker?.ready;
      if (reg?.sync) {
        await reg.sync.register('aweni-sync');
      }
    } catch {
      // Background sync not supported
    }
  }

  // ---- Helpers ------------------------------------------------------------

  private isNetworkError(err: unknown): boolean {
    if (err instanceof TypeError && err.message.includes('fetch')) return true;
    if (err instanceof Error && err.message.includes('NetworkError')) return true;
    return !navigator.onLine;
  }

  private sleep(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  private emit(type: SyncEventType, detail: unknown): void {
    this.emitter.dispatchEvent(new CustomEvent(type, { detail }));
  }
}

// ---------------------------------------------------------------------------
// Singleton
// ---------------------------------------------------------------------------

export const syncEngine = new SyncEngine();
